import {Component, ElementRef, OnInit} from '@angular/core';
import {forkJoin, Subject} from 'rxjs';
import {AdminPanelService} from '../../data/admin-panel.service';
import {debounceTime} from 'rxjs/operators';
import {PricingCategory} from '../../../project-calc/data/pricing-category';
import {MatDialog} from '@angular/material/dialog';
import {CategoryEditorDialogComponent} from './category-editor-dialog/category-editor-dialog.component';
import {PricingMultiplication} from '../../../project-calc/data/multiplication/pricing-multiplication';
import {MultiplicationEditorDialogComponent} from './multiplication-editor-dialog/multiplication-editor-dialog.component';
import {PricingMultiplicationTypes} from '../../../project-calc/data/multiplication/pricing-multiplication-type';
import {CategoryModifier} from '../../../project-calc/data/category-modifier';
import {ModifierEditorDialogComponent} from './category-editor-dialog/modifier-editor-dialog/modifier-editor-dialog.component';
import {ShortObject} from '../../../project-misc/data/project/short-json-object';
import {PricingMiscItem} from '../../../project-calc/data/misc-item/pricing-misc-item';
import {PricingMiscItemPreset} from '../../../project-calc/data/misc-item/pricing-misc-item-preset';
import {ProjectCalcService} from '../../../project-calc/data/project-calc.service';
import {PricingFee} from '../../../project-calc/data/fee/pricing-fee';
import {PricingFeeTypes} from '../../../project-calc/data/fee/pricing-fee-types';
import {FeeEditorDialogComponent} from './fee-editor-dialog/fee-editor-dialog.component';

export interface CategoryBlock {
  title: string;
  categories: PricingCategory[]
}

@Component({
  selector: 'app-pricing-categories-editor',
  templateUrl: './pricing-categories-editor.component.html',
  styleUrls: ['./pricing-categories-editor.component.scss']
})
export class PricingCategoriesEditorComponent implements OnInit {
  categories: PricingCategory[]
  categoriesWithTitle: CategoryBlock[] = [];

  multiplications: PricingMultiplication[]
  multiplicationTypes: PricingMultiplicationTypes

  modifiers: CategoryModifier[]

  loadingSpinner = true;

  updateItemsSubj = new Subject<number>()

  catMap = new Map<string, PricingCategory[]>();

  miscItems: PricingMiscItem[]
  miscItemPresets: PricingMiscItemPreset[]

  feeItems: PricingFee[]
  feeTypes: PricingFeeTypes

  constructor(
    protected el: ElementRef,
    protected dialog: MatDialog,
    protected adminPanelService: AdminPanelService,
    protected projectCalcService: ProjectCalcService
  ) {
  }

  ngOnInit(): void {
    forkJoin(
      this.adminPanelService.getCategories(),
      this.adminPanelService.getMultiplicationTypes(),
      this.adminPanelService.getMultiplications(),
      this.adminPanelService.getModifiers(),
      this.projectCalcService.getMiscItems(),
      this.projectCalcService.getMiscItemsPresets(),
      this.adminPanelService.getFeeList(),
      this.adminPanelService.getFeeTypes()).subscribe(result => {
      const categories = result[0]
      const types = result[1]
      const multiplication = result[2]
      const modifiers = result[3]
      const miscItems = result[4]
      const miscItemPresets = result[5]
      const feeItems = result[6]
      const feeTypes = result[7]

      this.categories = categories
      this.multiplicationTypes = types
      this.multiplications = multiplication
      this.modifiers = modifiers;
      this.miscItems = miscItems
      this.miscItemPresets = miscItemPresets
      this.feeItems = feeItems;
      this.feeTypes = feeTypes;

      this.sortByClazz()

      this.loadingSpinner = false;
    })

    this.updateItemsSubj.pipe(debounceTime(500)).subscribe(index => {
      this.adminPanelService.updateCategory(this.categories[index]).subscribe(() => {

      })
    })
  }

  getAffectingCategories() {
  }


  sortByClazz() {
    this.categories.forEach(cat => {
      if (!this.catMap.has(cat.categoryType)) {
        this.catMap.set(cat.categoryType, [])
      }

      this.catMap.get(cat.categoryType).push(cat)
    })

    this.catMap.forEach((val, key) => {
      this.categoriesWithTitle.push({
        title: key,
        categories: val
      })
    })
  }

  newCategory(type: string, index: number) {
    const newCategory = new PricingCategory()
    newCategory.categoryType = type

    const dialogRef = this.dialog.open(CategoryEditorDialogComponent, {
      data: {
        category: newCategory
      },
      panelClass: 'category-editor-dialog'
    });

    dialogRef.afterClosed().subscribe(newMod => {
      if (newMod && newMod instanceof PricingCategory) {
        this.categoriesWithTitle[index].categories.push(newMod)
      }
    })
  }

  editCategory(category: PricingCategory, index: number, jindex: number) {
    const dialogRef = this.dialog.open(CategoryEditorDialogComponent, {
      data: {
        category
      },
      panelClass: 'category-editor-dialog'
    });

    dialogRef.afterClosed().subscribe(newMod => {
      if (newMod && newMod instanceof PricingCategory) {
        this.categoriesWithTitle[index].categories[jindex] = newMod
      } else if (newMod === 'delete') {
        this.categoriesWithTitle[index].categories.splice(jindex, 1);
      }
    })
  }

  newMultiplication() {
    const dialogRef = this.dialog.open(MultiplicationEditorDialogComponent, {
      data: {
        modifier: new PricingMultiplication(),
        categories: this.categories,
        applyToFields: this.multiplicationTypes.applyToField,
        operations: this.multiplicationTypes.operation
      },
      panelClass: 'multiplication-editor-dialog'
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res && res instanceof PricingMultiplication) {
        this.multiplications.push(res)
      }
    })
  }


  editMultiplication(multiplication: PricingMultiplication, index: number) {
    const dialogRef = this.dialog.open(MultiplicationEditorDialogComponent, {
      data: {
        multiplication,
        categories: this.categories,
        applyToFields: this.multiplicationTypes.applyToField,
        operations: this.multiplicationTypes.operation
      },
      panelClass: 'multiplication-editor-dialog'
    });

    dialogRef.afterClosed().subscribe(newMult => {
      if (newMult && newMult instanceof PricingMultiplication) {
        this.multiplications[index] = newMult
      } else if (newMult === 'delete') {
        this.multiplications.splice(index, 1);
      }
    })
  }

  addNewModifier() {
    const newModifier = new CategoryModifier()
    newModifier.fields = [{
      pricingItemField: 'QTY',
      value: null
    }, {
      pricingItemField: 'DEPTH',
      value: null
    }, {
      pricingItemField: 'HEIGHT',
      value: null
    }, {
      pricingItemField: 'WIDTH',
      value: null
    }]

    const dialogRef = this.dialog.open(ModifierEditorDialogComponent, {
      data: {
        modifier: newModifier,
        miscList: this.miscItems,
        miscPresetsList: this.miscItemPresets,
        categories: this.categories
      },
      panelClass: 'nfs-sub-editor-dialog'
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res && res instanceof CategoryModifier) {
        this.modifiers.push(res)
      }
    })
  }

  editModifier(modifier: CategoryModifier, index: number) {
    const dialogRef = this.dialog.open(ModifierEditorDialogComponent, {
      data: {
        modifier,
        miscList: this.miscItems,
        miscPresetsList: this.miscItemPresets,
        categories: this.categories
      },
      panelClass: 'nfs-sub-editor-dialog'
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res && res instanceof CategoryModifier) {
        const fIndex = this.modifiers.findIndex(sub => sub.id === res.id)
        if (fIndex > -1) {
          this.modifiers[fIndex] = res
        }
      } else if (res === 'delete') {
        const fIndex = this.modifiers.indexOf(modifier, 0)

        if (fIndex > -1) {
          this.modifiers.splice(index, 1);
        }
      }
    })
  }

  addNewFee() {
    const newFee = new PricingFee()

    const dialogRef = this.dialog.open(FeeEditorDialogComponent, {
      data: {
        fee: newFee,
        feeTypes: this.feeTypes
      },
      panelClass: 'nfs-sub-editor-dialog'
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res && res instanceof PricingFee) {
        this.feeItems.push(res)
      }
    })
  }

  editFee(fee: PricingFee, index: number) {
    const dialogRef = this.dialog.open(FeeEditorDialogComponent, {
      data: {
        fee,
        feeTypes: this.feeTypes
      },
      panelClass: 'nfs-sub-editor-dialog'
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res && res instanceof PricingFee) {
        const fIndex = this.feeItems.findIndex(sub => sub.id === res.id)
        if (fIndex > -1) {
          this.feeItems[fIndex] = res
        }
      } else if (res === 'delete') {
        const fIndex = this.feeItems.indexOf(fee, 0)

        if (fIndex > -1) {
          this.feeItems.splice(index, 1);
        }
      }
    })
  }


}
